import { Head, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { EMPTY, humanize } from "@/components/products/fleet/format"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface ShopRow {
  id: number
  company: string
  display_name: string
  city: string | null
  state: string | null
  active: boolean
  capabilities: string[]
  notes: string | null
  closed_work_orders: number
  avg_days_in_shop: number | null
}

interface Props {
  shops: ShopRow[]
  filters: {
    q: string | null
    state: string | null
    active: string | null
  }
  filter_options: {
    states: string[]
  }
}

const ANY = "any"

export default function ShopsIndex({ shops, filters, filter_options }: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Shops", href: routes.shopsPath() },
  ]

  const [query, setQuery] = useState(filters.q ?? "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  function applyFilters(overrides: Partial<Props["filters"]>, delay = 0) {
    const next = { ...filters, q: query, ...overrides }
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(next)) {
      if (value && value !== ANY) params[key] = String(value)
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      router.get(routes.shopsPath(), params, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }, delay)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Shops" />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Shops</h1>
          <p className="text-muted-foreground text-sm">
            {shops.length} {shops.length === 1 ? "shop" : "shops"} in the repair
            network. Dwell is measured from the day a car arrived to the day it
            was released, over work we have actually closed there.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[14rem] flex-1">
            <Search
              className="text-muted-foreground absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search company or city"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                applyFilters({ q: e.target.value }, 400)
              }}
              className="h-9 pl-7"
              aria-label="Search shops"
            />
          </div>

          <Select
            value={filters.state ?? ANY}
            onValueChange={(value) => applyFilters({ state: value })}
          >
            <SelectTrigger
              className="h-9 w-[10rem] text-sm"
              aria-label="Filter by state"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All states</SelectItem>
              {filter_options.states.map((state) => (
                <SelectItem key={state} value={state}>
                  {state}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.active ?? ANY}
            onValueChange={(value) => applyFilters({ active: value })}
          >
            <SelectTrigger
              className="h-9 w-[10rem] text-sm"
              aria-label="Filter by status"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Retired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Capabilities</TableHead>
                <TableHead className="text-right">Closed WOs</TableHead>
                <TableHead className="text-right">Avg days in shop</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shops.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-muted-foreground py-8 text-sm"
                  >
                    No shops match these filters. Clear a filter to see the full
                    repair network.
                  </TableCell>
                </TableRow>
              ) : (
                shops.map((shop) => (
                  <TableRow key={shop.id}>
                    <TableCell className="font-medium">
                      {shop.company}
                    </TableCell>
                    <TableCell className="text-sm">
                      {[shop.city, shop.state].filter(Boolean).join(", ") ||
                        EMPTY}
                    </TableCell>
                    <TableCell>
                      {shop.capabilities.length === 0 ? (
                        <span className="text-muted-foreground text-sm">
                          {EMPTY}
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {shop.capabilities.map((capability) => (
                            <Badge
                              key={capability}
                              variant="secondary"
                              className="text-xs"
                            >
                              {humanize(capability)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {shop.closed_work_orders > 0 ? (
                        shop.closed_work_orders
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {/* No history is an em dash, never 0 — "we have never
                          sent them a car" is not "they turn cars in zero days". */}
                      {shop.avg_days_in_shop != null ? (
                        shop.avg_days_in_shop.toFixed(1)
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          shop.active
                            ? "border-emerald-500/40 text-xs text-emerald-600 dark:text-emerald-400"
                            : "border-slate-400/40 text-xs text-slate-600 dark:text-slate-400"
                        }
                      >
                        {shop.active ? "Active" : "Retired"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  )
}
